/* DashboardAlertStack — Setup/profile completeness alerts for the
 * doctor dashboard.
 *
 * iter100.178 — Consolidates the setup/profile nudges (onboarding
 * incomplete, business hours missing) into one visually consistent
 * surface at the top of the dashboard.
 *
 * iter100.179 — Planner-question alerts were REMOVED from this stack
 * because `NeedsMeNow` already renders them (it's the canonical
 * "case-action queue"). This stack now covers only setup/profile
 * completeness nudges — things that are NOT case actions and are NOT
 * surfaced elsewhere on the dashboard. Priority order:
 *
 *   1. Onboarding incomplete  — public profile hidden       (amber)
 *   2. Business hours missing — microsite shows "by appt"   (amber)
 *
 * Behaviour:
 *   - Renders NOTHING when no alerts are active.
 *   - Renders a single-alert compact banner (no wrapper header) when
 *     exactly one alert is active — the common case now that planner
 *     questions live in NeedsMeNow.
 *   - Renders the full stack with a "Setup checklist" header when 2+
 *     alerts are active (kept for future extensibility — e.g. adding
 *     "add first offer", "invite clinic staff", etc.).
 *
 * All existing data-testids are preserved so bookmarks / tests /
 * onboarding tours continue to resolve.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Warning, CheckSquare, CaretRight,
} from "@phosphor-icons/react";


const TONE_STYLES = {
    amber: {
        chip: "bg-amber-100 text-amber-800 ring-amber-200",
        icon: "text-amber-700",
        cta:  "bg-amber-600 hover:bg-amber-700 text-white",
        row:  "border-amber-100 hover:border-amber-200 bg-amber-50/60",
    },
};


/* Build the ordered list of active alerts from the raw dashboard inputs.
   Kept as a pure function so the caller can also derive the count for
   badges / analytics without re-implementing the priority rules. */
export function buildDoctorAlerts({
    doctorProfile,
    // iter100.263 — `onboardingOpen` and `onResumeOnboarding` were consumed
    // by the retired "public profile incomplete" alert. Kept in the API
    // signature so existing callers (Dashboard.jsx) don't need edits, but
    // they no longer drive any alert.
}) {
    const alerts = [];

    // iter100.263 — The old "Your public profile is incomplete" alert
    // (iter100.178) was retired here. `<DoctorOnboardingWidget>`
    // (iter100.261) now owns that surface at the top of the dashboard
    // with a proper lifecycle-aware checklist. Showing both banners
    // caused a "two banners saying the same thing" bug.

    // Business-hours nudge — only relevant after onboarding is done.
    if (doctorProfile && doctorProfile.onboarded_at) {
        const bh = doctorProfile.business_hours || {};
        const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
        // iter100.327 — Count the days that are ACTUALLY unset (missing
        // from the payload entirely). A day marked closed still counts
        // as "set" — the doctor explicitly said "I'm closed on Sun."
        // The misleading previous copy said "X of 7 weekdays" — which
        // read as "you're only OPEN X days" and lied when the doctor
        // was actually open ≥ 5 days but hadn't touched every row.
        const unsetDays = days.filter((d) => {
            const cfg = bh[d];
            return !cfg || typeof cfg !== "object" || (!cfg.closed && !(cfg.open && cfg.close));
        });
        if (unsetDays.length > 0) {
            const dayLabels = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
            const unsetLabels = unsetDays.map((d) => dayLabels[d]).join(", ");
            alerts.push({
                id: "business-hours-nudge",
                tone: "amber",
                Icon: Warning,
                title: `${unsetDays.length} day${unsetDays.length === 1 ? "" : "s"} of your hours aren't set yet`,
                description: `${unsetLabels} ${unsetDays.length === 1 ? "is" : "are"} showing "By appointment" on your public microsite. Set OPEN or CLOSED for each — takes about 30 seconds.`,
                cta: {
                    label: "Fix hours",
                    href: "/doctor/profile#business-hours",
                    testid: "business-hours-nudge-cta",
                },
                testid: "business-hours-nudge-banner",
            });
        }
    }

    return alerts;
}


function AlertRow({ alert, standalone = false }) {
    const tone = TONE_STYLES[alert.tone] || TONE_STYLES.amber;
    const wrapperClass = standalone
        ? `rounded-2xl border px-5 py-4 ${tone.row}`
        : "px-5 py-4";
    return (
        <div className={wrapperClass} data-testid={alert.testid}>
            <div className="flex items-start gap-3 flex-wrap">
                <alert.Icon size={18} weight="duotone" className={`${tone.icon} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground/90">
                        {alert.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {alert.description}
                    </div>
                </div>
                {alert.cta && (
                    alert.cta.href ? (
                        <Button
                            asChild
                            size="sm"
                            className={`rounded-full ${tone.cta}`}
                            data-testid={alert.cta.testid}
                        >
                            <Link to={alert.cta.href}>{alert.cta.label}</Link>
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={alert.cta.onClick}
                            className={`rounded-full ${tone.cta}`}
                            data-testid={alert.cta.testid}
                        >
                            {alert.cta.label}
                        </Button>
                    )
                )}
            </div>
        </div>
    );
}


export default function DashboardAlertStack(props) {
    const alerts = buildDoctorAlerts(props);
    if (alerts.length === 0) return null;

    // Single alert → render as a standalone card. This avoids the
    // "N items in setup checklist" ceremony for the common case (which
    // is what fires when a doctor is mid-onboarding).
    if (alerts.length === 1) {
        return (
            <div data-testid="dashboard-alert-stack">
                <AlertRow alert={alerts[0]} standalone />
            </div>
        );
    }

    // 2+ alerts → labeled stack. Reserved for future setup-completeness
    // additions (e.g. "add first offer", "invite clinic staff").
    const outerTone = TONE_STYLES[alerts[0].tone] || TONE_STYLES.amber;
    return (
        <div
            className="rounded-2xl border border-amber-100 bg-card overflow-hidden"
            data-testid="dashboard-alert-stack"
        >
            <div className="flex items-center gap-2 px-5 py-3 border-b border-border/60 bg-secondary/30">
                <CheckSquare size={14} weight="duotone" className={outerTone.icon} />
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground/80">
                    Setup checklist
                </div>
                <span
                    className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${outerTone.chip}`}
                    data-testid="dashboard-alert-stack-count"
                >
                    <CaretRight size={9} weight="bold" className="mr-0.5 -ml-0.5" />
                    {alerts.length} items to finish
                </span>
            </div>
            <div className="divide-y divide-border/60">
                {alerts.map((a) => (
                    <AlertRow key={a.id} alert={a} />
                ))}
            </div>
        </div>
    );
}
